import { DirectorEvent, EventType, QueueState } from '@/types/director.types';
import { CameraPreset } from '@/types/camera.types';
import { ShotData } from '@/types/shot.types';

/**
 * Manages sequential processing of Director events
 * Ensures only one animation runs at a time
 */
export class EventQueueManager {
  private queue: DirectorEvent[] = [];
  private currentEvent: DirectorEvent | null = null;
  private isBusy: boolean = false;
  private onStateChange?: (state: QueueState) => void;

  /**
   * Register callback for queue state changes (useful for debug UI)
   */
  public onStateChangeCallback(callback: (state: QueueState) => void): void {
    this.onStateChange = callback;
    this.notifyStateChange();
  }

  /**
   * Add a camera move event to the queue
   */
  public enqueueCameraMove(preset: CameraPreset): void {
    const event: DirectorEvent = {
      id: `camera-${Date.now()}`,
      type: EventType.CAMERA_MOVE,
      payload: preset,
    };
    this.queue.push(event);
    this.notifyStateChange();
    this.processNext();
  }

  /**
   * Add a shot trace event to the queue
   */
  public enqueueShotTrace(shot: ShotData): void {
    const event: DirectorEvent = {
      id: `shot-${shot.id}-${Date.now()}`,
      type: EventType.SHOT_TRACE,
      payload: shot,
    };
    this.queue.push(event);
    this.notifyStateChange();
    this.processNext();
  }

  /**
   * Process the next event in the queue if not busy
   */
  private processNext(): void {
    if (this.isBusy || this.queue.length === 0) {
      return;
    }

    const event = this.queue.shift()!;
    this.currentEvent = event;
    this.isBusy = true;
    this.notifyStateChange();

    // Event will be executed by consumer (CourseViewer)
    // They must call markCurrentEventComplete() when done
  }

  /**
   * Get the current event to be processed
   * Returns null if no event is ready
   */
  public getCurrentEvent(): DirectorEvent | null {
    return this.currentEvent;
  }

  /**
   * Mark the current event as complete and process the next one
   */
  public markCurrentEventComplete(): void {
    if (!this.isBusy) {
      console.warn('markCurrentEventComplete called but queue is not busy');
      return;
    }

    this.currentEvent = null;
    this.isBusy = false;
    this.notifyStateChange();

    // Process next event in queue
    this.processNext();
  }

  /**
   * Check if queue is currently processing an event
   */
  public isProcessing(): boolean {
    return this.isBusy;
  }

  /**
   * Get current queue state for debugging
   */
  public getState(): QueueState {
    return {
      queue: [...this.queue],
      currentEvent: this.currentEvent,
      isBusy: this.isBusy,
    };
  }

  /**
   * Clear all queued events (does not stop current event)
   */
  public clear(): void {
    this.queue = [];
    this.notifyStateChange();
  }

  /**
   * Reset queue to initial state (clears queue and current event)
   */
  public reset(): void {
    this.queue = [];
    this.currentEvent = null;
    this.isBusy = false;
    this.notifyStateChange();
    console.log('[EventQueueManager] Reset complete');
  }

  /**
   * Notify listeners of state change
   */
  private notifyStateChange(): void {
    if (this.onStateChange) {
      this.onStateChange(this.getState());
    }
  }

  /**
   * Get queue length
   */
  public getQueueLength(): number {
    return this.queue.length;
  }
}
